<script setup lang="ts">
import {ElBadge, ElCheckboxButton, ElCheckboxGroup, ElSkeleton, ElSkeletonItem} from 'element-plus';
import SelectAllIcon from '@/views/icons/SelectAllIcon.vue';
import SelectNoneIcon from '@/views/icons/SelectNoneIcon.vue';
import {computed, nextTick, onMounted, PropType, ref, watch} from 'vue';
import {CabReportItem} from 'common';
import {badgeOffset, localStorageGetJson, localStorageSetJson, SelectGroupInfo} from '@/base/utils.js';
import {done} from '@/base/loadingState.js';

const emit  = defineEmits(['update:selectedList']);
const props = defineProps({
    all: {
        type    : Array as PropType<CabReportItem[]>,
        required: true,
    },
    names: {
        type    : Array as PropType<string[]>,
        required: true,
    },
    inverseOrder: {
        type   : Boolean,
        default: false,
    },
    localStorageName: {
        type    : String,
        required: true,
    },
    selectField: {
        type    : Function as PropType<(_a: CabReportItem) => SelectGroupInfo>,
        required: true,
    },
    selectedList: {
        type    : Array as PropType<(string)[]>,
        required: true,
    },
    getBadgeValue: {
        type    : Function as PropType<(_a: string) => number | undefined>,
        required: true,
    },
    badgeColor: {
        type    : String as PropType<string | undefined>,
        required: true,
        default : undefined,
    },
    getExtraBadgeValue: {
        type   : Function as PropType<(_a: string) => number | undefined>,
        default: () => undefined,
    },
    extraBadgeColor: {
        type   : String as PropType<string | undefined>,
        default: undefined,
    },
    highlightId: {
        type   : String as PropType<string | undefined>,
        default: undefined,
    },
});

const map             = computed<Map<string, SelectGroupInfo>>(() => new Map(props.all?.map((item: CabReportItem) => [props.selectField(item).id, props.selectField(item)])));
const isOneOption     = computed<boolean>(() => map.value.size === 1);
const allOptions      = computed<(string)[]>(() => Array.from(map.value.values()).sort((a, b) => a.name.localeCompare(b.name) * (props.inverseOrder ? -1 : 1)).map(e => e.id));
const selectedOptions = ref<string[]>([]);
const viewDone        = ref<boolean>(false);

watch(selectedOptions, newVal => {
    emit('update:selectedList', newVal);
    localStorageSetJson(props.localStorageName, newVal);
});
watch(() => props.selectedList, newVal => {
    selectedOptions.value = newVal;
});
watch(allOptions, (newAll, oldAll) => {
    if (!newAll || newAll.length === 0) {
        selectedOptions.value = [];
        return;
    }

    if (newAll.length === 1) {
        selectedOptions.value = newAll;
        return;
    }

    // Initial load (oldAll is undefined)
    if (oldAll === undefined) {
        const previouslySelected: (string)[] | undefined = localStorageGetJson(props.localStorageName);
        // If previouslySelected is undefined (never set), select all by default
        // If previouslySelected is [] (user explicitly deselected all), keep it empty
        if (previouslySelected === undefined) {
            selectedOptions.value = newAll;
        } else if (previouslySelected.length === 0) {
            selectedOptions.value = [];
        } else {
            // keep only the ones that are still in the current list
            const restoreList     = previouslySelected.filter((id: string) => newAll.includes(id));
            selectedOptions.value = restoreList.length > 0 ? restoreList : newAll;
        }
        return;
    }

    // Options changed (e.g. reporter filter changed) - keep valid selections, or select all if none valid
    if (arraysDiffer(oldAll, newAll)) {
        const newAllSet       = new Set(newAll);
        const stillValid      = selectedOptions.value.filter(id => newAllSet.has(id));
        selectedOptions.value = stillValid.length > 0 ? stillValid : newAll;
    }
},
{immediate: true},
);
onMounted(() => {
    nextTick(() => viewDone.value = true);
});

function arraysDiffer(a: string[] | undefined, b: string[] | undefined): boolean {
    if (!a) {
        return !!b;
    } else {
        if (!b) {
            return true;
        } else {
            return a.length !== b.length || !a.every((val, i) => val === b[i]);
        }
    }
}

function myBadgeOffset(extra: boolean, id: string): [number, number] {
    return badgeOffset([0, -11], extra, props.getBadgeValue(id), props.getExtraBadgeValue ? props.getExtraBadgeValue(id) : undefined);
}

</script>

<template>
    <div class="flex flex-row px-10 w-full h-16">
        <div class="w-52 flex-shrink-0">
            <div class="pl-2 pr-4 py-2 my-1 text-right font-thin flex items-center justify-end gap-1">
                <span>{{ names[isOneOption ? 0 : 1] }}</span>
                <div v-if="!isOneOption" class="flex flex-col">
                    <div
                        @click="selectedOptions = [...allOptions]"
                        :class="selectedOptions.length === allOptions.length ? 'cursor-default opacity-30' : 'cursor-pointer'"
                        title="Selecteer alles"
                    >
                        <SelectAllIcon/>
                    </div>
                    <div
                        @click="selectedOptions = []"
                        :class="selectedOptions.length === 0 ? 'cursor-default opacity-30' : 'cursor-pointer'"
                        title="Selecteer niets"
                    >
                        <SelectNoneIcon/>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex-1 py-2">
            <el-skeleton class="w-full" animated v-if="!done">
                <template #template>
                    <el-skeleton-item variant="rect" animated style="width: 340px; height: 34px"/>
                </template>
            </el-skeleton>

            <template v-else>
                <div v-if="isOneOption" class="pt-1">
                    {{ map.get(allOptions[0])?.name }}
                </div>
                <template v-else>
                    <el-checkbox-group v-model="selectedOptions" class="flex">
                        <el-checkbox-button :value="id" v-for="id in allOptions"
                                            :key="id"
                                            border
                        >
                            <el-badge
                                :value="getBadgeValue(id)"
                                :color="badgeColor"
                                :offset="myBadgeOffset(false, id)"
                                badge-class="font-normal"
                            >
                                <el-badge
                                    :value="getExtraBadgeValue(id)"
                                    :color="extraBadgeColor"
                                    :offset="myBadgeOffset(true, id)"
                                    :show-zero="false"
                                    badge-class="font-normal"
                                >
                                    <span :class="highlightId === id ? 'text-blue-600 font-semibold' : ''">
                                        {{ map.get(id)?.name }}
                                    </span>
                                </el-badge>
                            </el-badge>
                        </el-checkbox-button>
                    </el-checkbox-group>
                </template>
            </template>
        </div>
    </div>
</template>
